import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Company } from '@models/companies/company';
import { SecurityChange } from '@models/documents/security-change';
import { SecurityRegistryRecord } from '@models/shares/security-registry-record';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { ShareGroupView } from "@pages/shares/models/share-group-view";
import {
  CommonActionsModalComponent,
  IActionButtonConfig
} from "@shared/components/commons/common-actions-modal/common-actions-modal.component";
import { SecurityApiService } from '@shared/services/security-api.service';
import { getSharesPagePath } from "@shared/utils/route-path-utils";
import { DatepickerHelper } from '@shared/validators/date-picker-form-validators';
import { finalize, of } from 'rxjs';
import { CertificateConsolidationOptions } from '../../../models/shares/company-security-issue.model';
import { ModalFormsService } from "../../../services/modal-forms.service";
import { filterRecordsWithEmptyHoldings } from "../../../utils/filter-records-with-empty-holdings";
import { BaseStepperFormComponent } from "../../stepper-form";

@Component({
  selector: 'app-base-share-form',
  standalone: true,
  template: '',
})
export class BaseShareFormComponent<T, Change extends SecurityChange<Company>> extends BaseStepperFormComponent<T, Change> implements OnInit {
  modalFormsService = inject(ModalFormsService);
  securityService = inject(SecurityApiService);
  cdr = inject(ChangeDetectorRef);

  readonly certificateConsolidationOptions = CertificateConsolidationOptions;

  securityRegistryRecords: SecurityRegistryRecord[] = [];
  authorisedSecurityRegistryRecords: SecurityRegistryRecord[] = [];
  securityRegistryAsOfDate: Date | undefined;
  minDate!: NgbDateStruct;
  maxDate!: NgbDateStruct;
  isEstimateLoading = false;
  shouldLoadEstimate = true;
  estimatedSecurityRegistryRecords: SecurityRegistryRecord[] = [];
  hasShareIssue = false;
  isShareIssueModalOpened = false;

  prefillData: ShareGroupView | null = null;
  shouldPrefillSecurityType = false;
  currentSecurityTransactionIds: string[] = [];

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.minDate = DatepickerHelper.getDateOfEstablishmentMinDate(this.companyChangeData.dateOfEstablishment);
    this.maxDate = DatepickerHelper.getNextNYearsStruct(10);
    this.setFormValidators();
    this.setupFormChangeListeners();
    this.loadShareholders();
  }

  setFormValidators(): void {
  }

  setupFormChangeListeners(): void {
    this.listenStepperFormChanges();
  }

  override afterSubmit(changes: SecurityChange<Company>[]): void {
    this.setupChange(changes[0] as SecurityChange<Company>);
  }

  override setupChange(change: SecurityChange<Company> = this.formModel): void {
  }

  loadShareholders(asOfDate: Date = new Date(), isReload = false): void {
    if (this.securityRegistryAsOfDate && asOfDate.toISOString() === this.securityRegistryAsOfDate.toISOString()) {
      return;
    }

    this.isLoading = true;
    this.securityRegistryAsOfDate = asOfDate;

    const excludedTransactionsIds = this.currentSecurityTransactionIds.length ? this.currentSecurityTransactionIds : null;

    this.securityService.getSecurityRegistryRecords(
      this.companyChangeData.entityId,
      true,
      this.excludedDocumentId,
      true,
      asOfDate,
      excludedTransactionsIds
    )
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: (records) => {
          const filteredRecords = filterRecordsWithEmptyHoldings(records);
          this.onShareholdersLoadSuccess(filteredRecords, isReload);
          this.handleShareIssue();
        },
        error: (error) => {
          console.warn('[BaseShareFormComponent] Load Shareholders Error', error);
        }
      });
  }

  onShareholdersLoadSuccess(records: SecurityRegistryRecord[], isReload = false): void {
  }

  loadEstimates(): void {
    this.isEstimateLoading = true;
    this.securityService.getSecurityEstimate(this.companyChangeData.entityId, [this.buildShareFormChange().prepareToRequest()], this.excludedDocumentId)
      .pipe(finalize(() => {
        this.isEstimateLoading = false;
        this.shouldLoadEstimate = false;
      }))
      .subscribe({
        next: (estimatedSecurityRegistryRecords) => {
          this.onEstimateLoadSuccess(estimatedSecurityRegistryRecords);
        },
        error: (error: HttpErrorResponse) => {
          console.warn('[BaseShareFormComponent.loadEstimates] Load Estimates Error', error);
          const errorMessage = error.error?.toString() ?? '';
          let shownErrorMessage = 'Failed to load estimates';

          if (errorMessage.includes('Price override deviation is not in allowed bounds')) {
            console.warn('[BaseShareFormComponent.loadEstimates] Price override deviation is not in allowed bounds');
            shownErrorMessage = 'Price override deviation is not in allowed bounds';
          } else if (errorMessage.includes("Negative changes in transaction when positive were expected")) {
            console.warn('[BaseShareFormComponent.loadEstimates] Negative changes in transaction when positive were expected');
            shownErrorMessage = 'Negative changes in transaction when positive were expected';
          } else if (errorMessage.includes("Positive changes in transaction when negative were expected")) {
            console.warn('[BaseShareFormComponent.loadEstimates] Positive changes in transaction when negative were expected');
            shownErrorMessage = 'Positive changes in transaction when negative were expected';
          } else {
            console.warn('[BaseShareFormComponent.loadEstimates] Unknown Message');
          }

          this.toastr.error(shownErrorMessage, 'Error');
        }
      });
  }

  onEstimateLoadSuccess(estimatedSecurityRegistryRecords: SecurityRegistryRecord[]): void {
  }

  buildShareFormChange(): SecurityChange<Company> {
    throw new Error('Method buildShareFormChange not implemented.');
  }

  isAuthorisedSignatoriesStepVisible(): boolean {
    return false;
  }

  fillAuthorisedSignatories(): void {
    this.authorisedSecurityRegistryRecords = [];
  }

  prefillForm(shareGroupView = this.prefillData, shouldPrefillSecurityType = this.shouldPrefillSecurityType): void {
    this.prefillData = null;
    this.shouldPrefillSecurityType = false;
  }

  handleShareIssue(): void {
    if (!this.hasShareIssue || this.isShareIssueModalOpened) return;

    this.isShareIssueModalOpened = true;

    const message = `
        <div class="text-center">Share issues have been identified for this company.</div>
        <div class="text-center">Click ‘Resolve’ to review and fix the details.</div>
    `;
    const modalRef = this.modalFormsService.openModal(CommonActionsModalComponent, { size: 'sm', centered: true });
    const instance = modalRef.componentInstance as CommonActionsModalComponent;

    const actions: IActionButtonConfig[] = [
      {
        text: 'Resolve',
        iconClass: 'icon ic-resolve-20',
        action: of(true)
      },
      {
        text: 'Cancel',
        btnColor: 'white',
        action: of(false)
      }
    ];

    instance.config = {
      ...instance.config,
      actions,
      iconClass: 'ic-alert-triangle-20 bg-red',
      header: 'Share Issues Found',
      body: message,
    };
    instance['forceClose'] = true;

    modalRef.result.then(
      (res: boolean) => {
        const result = res ?? false;
        this.modalFormsService.closeAllFormsModals();

        if (result) {
          void this.router.navigate(getSharesPagePath(this.companyChangeData.entityId));
        }
      },
      () => this.modalFormsService.closeAllFormsModals()
    );
  }

  private listenStepperFormChanges(): void {
    this.stepperForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.shouldLoadEstimate = true);
  }

  get excludedDocumentId(): string {
    return this.isEdit && this.companyChangeData.documentId ? this.companyChangeData.documentId : '';
  }

  static isCompanyIncludeShareIssue(company: Company | null | undefined): boolean {
    return Boolean(company?.systemTags.find((tag) => tag.class == 'system-red' && tag.description == 'Share Issue'));
  }
}
